import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { decodeJwtPayload, readSupabaseTokensFromLocalStorage } from '../../lib/supabaseAuth';
import { CircularProgress, Box } from '@mui/material';

/**
 * RoleBasedRedirect - Redirects users to their appropriate landing page based on role
 *
 * SECURITY: Waits for fresh data from API before making routing decisions.
 * Never uses cached/stale values to prevent cross-user data leakage.
 *
 * Routing logic:
 * - Super Admin → /admin (Admin Console) - has access to ALL tenants
 * - Admin → /admin (Admin Console) - has access to assigned tenants only
 * - Accounting → /accounting (Accounting Portal)
 * - Insurance → /partner (Insurance Partner Portal)
 * - Read-only → /reports/opentrack (OpenTrack Reports)
 * - No role / Unknown → /reports/opentrack (fallback)
 */
export const RoleBasedRedirect: React.FC = () => {
  const { user, loading, isSuperAdmin, superAdminLoading, role, roleLoading } = useAuth();

  // Wait for auth to complete
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // aal1 session: MFA is the required next step — never wait on roles
  // (role fetch is aal2-gated and will never arrive). AuthMfa forwards
  // no-factor users to /setup-mfa, so it covers both enrollment states.
  // Reads PW_ACCESS_TOKEN with SDK-key fallback: flow-1 arrivals may hold
  // an SDK-only session that PW_ACCESS_TOKEN alone misses.
  const { accessToken: anyToken } = readSupabaseTokensFromLocalStorage(
    (import.meta as any).env?.VITE_SUPABASE_URL,
  );
  const tokenAal = anyToken ? decodeJwtPayload(anyToken)?.aal : undefined;
  if (tokenAal === 'aal1') {
    return <Navigate to="/auth/mfa" replace />;
  }

  // SECURITY: Wait for ALL role/permission data to load from API
  if (roleLoading || superAdminLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Route based on confirmed role
  if (isSuperAdmin || role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  if (role === 'accounting') {
    return <Navigate to="/accounting" replace />;
  }

  if (role === 'insurance') {
    return <Navigate to="/partner" replace />;
  }

  if (role === 'readonly') {
    return <Navigate to="/reports/covered-parts" replace />;
  }

  // No recognized role yet — every valid user has a role, keep waiting
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};
